import React, { useEffect, useRef, useState } from "react";
import { motion } from "../../lib/motionSafe"; // polku suhteessa komponenttiin
import { ArrowUp } from "lucide-react";

/**
 * Kelluva "takaisin ylös" -nappi. Pysyy samalla linjalla vasemman
 * "Evästeet"-napin kanssa, koska molemmat käyttävät samaa
 * bottom-laskentaa (safe-area + mahdollinen dock + cookie-banner).
 */
export default function DiscoScrollTop({
  variant = "disco",
  label = "Ylös",
  size = 72,
  bottom = 16,              // ⬅️ Sama perus-bottom kuin CookieConsentissa
  right = 16,
  className = "",
  idleDelay = 600,
  baseOpacity = 0.6,
}) {
  const [visible, setVisible] = useState(false);
  const idleT = useRef(null);

  // Nostot: cookie-bannerin ja mahdollisen alapalkin (dock) korkeus
  const [liftCookie, setLiftCookie] = useState(0);
  const [liftDock, setLiftDock] = useState(0);

  const goTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // Näy vain kun rullaus on "idle"
  useEffect(() => {
    const kick = () => {
      setVisible(false);
      if (idleT.current) clearTimeout(idleT.current);
      idleT.current = setTimeout(() => setVisible(true), idleDelay);
    };
    kick();
    const onScroll = () => kick();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (idleT.current) clearTimeout(idleT.current);
    };
  }, [idleDelay]);

  // Kuuntele cookie-bannerin korkeutta + dockin korkeutta
  useEffect(() => {
    const onCookieHeight = (e) => {
      const h = Number(e.detail?.height || 0);
      setLiftCookie(h > 0 ? h + 8 : 0);
    };
    const onDock = (e) => {
      const h = Number(e.detail?.height || 0);
      setLiftDock(h > 0 ? h + 8 : 0);
    };
    const onCookieClosed = () => setLiftCookie(0);

    window.addEventListener("cookie-banner-height", onCookieHeight);
    window.addEventListener("cookie-consent-closed", onCookieClosed);
    window.addEventListener("bottom-dock-height", onDock);
    return () => {
      window.removeEventListener("cookie-banner-height", onCookieHeight);
      window.removeEventListener("cookie-consent-closed", onCookieClosed);
      window.removeEventListener("bottom-dock-height", onDock);
    };
  }, []);

  // Yhteinen bottom (sama kaava kuin vasemman napin hallinnassa)
  const safeBottom = `calc(${bottom}px + env(safe-area-inset-bottom) + ${liftCookie}px + ${liftDock}px)`;

  const motionCommon = {
    initial: { opacity: 0, y: 8 },
    animate: { opacity: visible ? baseOpacity : 0, y: visible ? 0 : 8 },
    whileHover: visible ? { scale: 1.06, opacity: 1 } : {},
    whileTap: { scale: 0.97 },
    transition: { duration: 0.25, ease: "easeOut" },
  };

  const commonBtnClasses = [
    "fixed z-[60] select-none transition",
    visible ? "pointer-events-auto" : "pointer-events-none",
    className,
  ].join(" ");

  // --- EQ-variantti (tekstiläpyskä) ---
  if (variant === "eq") {
    return (
      <motion.button
        onClick={goTop}
        aria-label="Siirry sivun alkuun"
          className={[
            commonBtnClasses,
            "rounded-full shadow-2xl backdrop-blur-md h-11 px-4",
            "bg-white/85 text-black border border-black/10",
            "dark:bg-black/80 dark:text-white dark:border-white/10",
            "flex items-center gap-3",
          ].join(" ")}

        style={{
          bottom: safeBottom,
          right: "max(16px, env(safe-area-inset-right))",
        }}
        {...motionCommon}
      >
        <div className="flex items-end gap-[3px] h-5">
          <EqBar delay={0} />
          <EqBar delay={0.15} />
          <EqBar delay={0.3} />
          <EqBar delay={0.45} />
        </div>
        <span className="font-semibold tracking-wide">{label}</span>
        <ArrowUp className="w-4 h-4 opacity-90" />
      </motion.button>
    );
  }

  // --- Disco ball -variantti ---
  const d = size;
  return (
    <motion.button
      onClick={goTop}
      aria-label="Siirry sivun alkuun"
      className={[
        commonBtnClasses,
        "rounded-full overflow-hidden ring-2 shadow-2xl",
        "bg-transparent ring-black/10 shadow-[0_0_28px_rgba(99,102,241,0.25),0_0_60px_rgba(59,130,246,0.20)]",
        "dark:ring-white/40 dark:shadow-[0_0_30px_rgba(255,45,149,0.35),0_0_60px_rgba(49,255,217,0.25)]",
      ].join(" ")}
      style={{
        bottom: safeBottom,
        right: "max(16px, env(safe-area-inset-right))",
        width: d,
        height: d,
      }}
      {...motionCommon}
    >
      <motion.div
        className="w-full h-full rounded-full relative 
                   bg-[radial-gradient(140%_120%_at_30%_25%,rgba(255,255,255,0.95)_0%,rgba(255,255,255,0.35)_18%,rgba(99,102,241,0.35)_44%,rgba(59,130,246,0.30)_70%,rgba(0,0,0,0.25)_100%)] 
                   dark:bg-[radial-gradient(140%_120%_at_30%_25%,rgba(255,255,255,0.85)_0%,rgba(255,255,255,0.15)_18%,rgba(255,45,149,0.40)_44%,rgba(49,255,217,0.35)_70%,rgba(0,0,0,0.55)_100%)]"
        animate={{ rotate: [0, 360] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      >
        <div
          className="absolute inset-0 rounded-full mix-blend-overlay opacity-70"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, rgba(255,255,255,0.35) 0 2px, transparent 2px 10px), repeating-linear-gradient(90deg, rgba(255,255,255,0.35) 0 2px, transparent 2px 10px)",
            filter: "drop-shadow(0 0 6px rgba(255,255,255,0.2))",
          }}
        />
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 210deg, rgba(255,255,255,0) 0deg, rgba(255,255,255,0.6) 40deg, rgba(255,255,255,0) 80deg)",
            mixBlendMode: "screen",
            opacity: 0.6,
          }}
          animate={{ rotate: [0, 360] }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        />
      </motion.div>
    </motion.button>
  );
}

function EqBar({ delay = 0 }) {
  return (
    <motion.span
      className="w-[3px] rounded-sm 
                 bg-gradient-to-b from-indigo-500 to-sky-400 
                 dark:from-primary dark:to-fuchsia-400"
      initial={{ height: 4 }}
      animate={{ height: [6, 18, 9, 14, 6] }}
      transition={{ duration: 1.1, repeat: Infinity, ease: "easeInOut", delay }}
    />
  );
}
